import React, { useContext, useState, useEffect, useMemo, useRef } from 'react';
import {
    Typography, Card, Input, Button, Select, Row, Col, Table, message, Tag, Grid, Divider, Modal,
} from 'antd';
import { WarningOutlined, SendOutlined, RetweetOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { SimulatorContext } from 'store/context';
import { generateUniqSerial, sendDTCSignal } from 'mqtt/aws';
import { useMutation } from '@tanstack/react-query';
import { FaNetworkWired } from 'react-icons/fa6';
import { BsSpeedometer2 } from 'react-icons/bs';
import { GiCarWheel, GiGearStick } from 'react-icons/gi';
import { TbCar, TbBolt, TbWind, TbLeaf } from 'react-icons/tb';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { useBreakpoint } = Grid;

const ANIMATION_CSS = `
  @keyframes dtc-rec-in {
    from { opacity: 0; transform: translateX(-16px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  @keyframes dtc-gauge-in {
    from { opacity: 0; transform: scale(0.82); }
    to   { opacity: 1; transform: scale(1); }
  }
  @keyframes dtc-pulse-warn {
    0%, 100% { opacity: 0.05; }
    50%       { opacity: 0.20; }
  }
  @keyframes dtc-score-num {
    from { opacity: 0; transform: translateY(6px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes dtc-chip-pop {
    0%   { transform: scale(0.7); opacity: 0; }
    70%  { transform: scale(1.08); opacity: 1; }
    100% { transform: scale(1); opacity: 1; }
  }
`;

const SAMPLE_DTCS = [
    { code: 'P0300', description: 'Random/Multiple Cylinder Misfire Detected' },
    { code: 'P0171', description: 'System Too Lean (Bank 1)' },
    { code: 'P0420', description: 'Catalyst System Efficiency Below Threshold (Bank 1)' },
    { code: 'P0442', description: 'Evaporative Emission System Leak Detected (Small Leak)' },
    { code: 'P0128', description: 'Coolant Thermostat Below Thermostat Regulating Temperature' },
    { code: 'P0455', description: 'Evaporative Emission System Leak Detected (Gross Leak)' },
    { code: 'P0113', description: 'Intake Air Temperature Sensor 1 Circuit High' },
    { code: 'P0340', description: "Camshaft Position Sensor 'A' Circuit (Bank 1 or Single Sensor)" },
    { code: 'P0500', description: 'Vehicle Speed Sensor' },
    { code: 'P0030', description: 'HO2S Heater Control Circuit (Bank 1 Sensor 1)' },
    { code: 'P0102', description: 'Mass or Volume Air Flow Circuit Low Input' },
    { code: 'P0122', description: 'Throttle/Pedal Position Sensor/Switch A Circuit Low Input' },
    { code: 'P0135', description: 'O2 Sensor Heater Circuit (Bank 1 Sensor 1)' },
    { code: 'P0201', description: 'Injector Circuit - Cylinder 1' },
    { code: 'P0230', description: 'Fuel Pump Primary Circuit' },
    { code: 'P0327', description: 'Knock Sensor 1 Circuit Low (Bank 1 or Single Sensor)' },
    { code: 'P0401', description: 'Exhaust Gas Recirculation Flow Insufficient Detected' },
    { code: 'P0507', description: 'Idle Control System RPM Higher Than Expected' },
    { code: 'P0603', description: 'Internal Control Module Keep Alive Memory (KAM) Error' },
    { code: 'P0705', description: 'Transmission Range Sensor Circuit Malfunction (PRNDL Input)' },
];

const STATUS_CONFIG = {
    Active: { color: 'error', bg: '#fff5f5', border: '#fca5a5', text: '#dc2626', hex: '#ef4444' },
    Pending: { color: 'warning', bg: '#fffbeb', border: '#fde68a', text: '#d97706', hex: '#f59e0b' },
    Inactive: { color: 'success', bg: '#f0fdf4', border: '#86efac', text: '#16a34a', hex: '#22c55e' },
    History: { color: 'processing', bg: '#eff6ff', border: '#bfdbfe', text: '#2563eb', hex: '#3b82f6' },
};

const VEHICLE_SYSTEMS = [
    { key: 'air_fuel', label: 'Air & Fuel', Icon: TbWind, prefix: 'P', min: 100, max: 299 },
    { key: 'ignition', label: 'Ignition', Icon: TbBolt, prefix: 'P', min: 300, max: 399 },
    { key: 'emissions', label: 'Emissions', Icon: TbLeaf, prefix: 'P', min: 400, max: 499 },
    { key: 'speed', label: 'Speed / Idle', Icon: BsSpeedometer2, prefix: 'P', min: 500, max: 699 },
    { key: 'trans', label: 'Transmission', Icon: GiGearStick, prefix: 'P', min: 700, max: 999 },
    { key: 'body', label: 'Body', Icon: TbCar, prefix: 'B', min: 0, max: 9999 },
    { key: 'chassis', label: 'Chassis', Icon: GiCarWheel, prefix: 'C', min: 0, max: 9999 },
    { key: 'network', label: 'Network', Icon: FaNetworkWired, prefix: 'U', min: 0, max: 9999 },
];

function computeHealthScore(active, pending, inactive, history, total) {
    if (total === 0) return 100;
    const deduction =
        Math.min(active, 5) * 8 + Math.max(0, active - 5) * 2 +
        Math.min(pending, 5) * 4 +
        inactive * 1.2 +
        history * 0.5;
    return Math.max(15, Math.round(100 - deduction));
}

function getHealthLabel(score) {
    if (score >= 90) return 'Excellent';
    if (score >= 75) return 'Good';
    if (score >= 55) return 'Fair';
    if (score >= 35) return 'Warning';
    return 'Critical';
}

function getHealthColor(score) {
    if (score >= 90) return '#16a34a';
    if (score >= 75) return '#22c55e';
    if (score >= 55) return '#f59e0b';
    if (score >= 35) return '#f97316';
    return '#ef4444';
}

const SYSTEM_RECOMMENDATIONS = [
    {
        key: 'air_fuel', label: 'Air & Fuel System', Icon: TbWind, prefix: 'P', min: 100, max: 299,
        actions: {
            Active: ['Replace or clean the air filter immediately', 'Test MAF sensor signal with a scan tool', 'Inspect fuel injectors for clogging or spray pattern issues'],
            Pending: ['Monitor short-term and long-term fuel trim values', 'Check intake hoses for cracks or air leaks', 'Schedule air filter inspection at next service'],
            Inactive: ['Log conditions when fault occurred for trend analysis', 'Inspect air filter at the next scheduled service'],
            History: ['Review fuel system maintenance records', 'Consider professional injector cleaning service'],
        },
    },
    {
        key: 'ignition', label: 'Ignition System', Icon: TbBolt, prefix: 'P', min: 300, max: 399,
        actions: {
            Active: ['Replace spark plugs immediately — check for fouling or wear', 'Test ignition coils under load with a coil tester', 'Perform cylinder compression test to check for damage'],
            Pending: ['Inspect spark plug condition, gap, and electrode wear', 'Monitor misfire event counters using OBD scan tool', 'Check coil-on-plug boots for cracks or carbon tracking'],
            Inactive: ['Note engine load and temperature when fault appeared', 'Inspect plugs at the next service interval'],
            History: ['Review ignition system maintenance records'],
        },
    },
    {
        key: 'emissions', label: 'Emissions & EGR', Icon: TbLeaf, prefix: 'P', min: 400, max: 499,
        actions: {
            Active: ['Inspect catalytic converter for physical damage or poisoning', 'Clean or replace the EGR valve — check for carbon buildup', 'Test upstream and downstream O2 sensors for correct voltage swing'],
            Pending: ['Check for exhaust leaks upstream of the oxygen sensors', 'Monitor catalyst efficiency live data with scan tool', 'Clean EGR passages to restore proper flow rate'],
            Inactive: ['Vehicle may still require emissions re-certification', 'Monitor exhaust emissions under load conditions'],
            History: ['Confirm repair effectiveness at next scheduled inspection'],
        },
    },
    {
        key: 'speed', label: 'Speed & Idle Control', Icon: BsSpeedometer2, prefix: 'P', min: 500, max: 699,
        actions: {
            Active: ['Inspect vehicle speed sensor connector and wiring harness', 'Check idle air control valve for sticking or carbon deposits', 'Clean throttle body — inspect for deposits around butterfly'],
            Pending: ['Monitor idle RPM stability after full engine warm-up', 'Check for vacuum leaks at the throttle body intake', 'Test throttle position sensor sweep for dead spots'],
            Inactive: ['Log idle RPM readings for recurring patterns'],
            History: ['Review ECU keep-alive memory and adaptive values'],
        },
    },
    {
        key: 'trans', label: 'Transmission', Icon: GiGearStick, prefix: 'P', min: 700, max: 999,
        actions: {
            Active: ['Check transmission fluid level and condition immediately', 'Inspect shift solenoids and their wiring harness connectors', 'Consult a transmission specialist — do not defer this repair'],
            Pending: ['Monitor gear shift quality and note any slippage events', 'Schedule transmission fluid and filter change', 'Perform a full transmission scan for additional codes'],
            Inactive: ['Continue monitoring shift behaviour under varied loads'],
            History: ['Confirm transmission fluid service is up to date'],
        },
    },
    {
        key: 'body', label: 'Body Systems', Icon: TbCar, prefix: 'B', min: 0, max: 9999,
        actions: {
            Active: ['Schedule comprehensive body electronics diagnostic scan', 'Inspect airbag system integrity and clock spring connector', 'Check body control module for corrosion or water ingress'],
            Pending: ['Inspect body control module connector pins for resistance', 'Test comfort system actuators (window, mirror, seat motors)'],
            Inactive: ['Monitor comfort systems for intermittent fault recurrence'],
            History: ['Review body module software update history'],
        },
    },
    {
        key: 'chassis', label: 'Chassis Systems', Icon: GiCarWheel, prefix: 'C', min: 0, max: 9999,
        actions: {
            Active: ['Inspect all wheel speed sensor gaps and tone rings immediately', 'Check ABS control module and wiring harness for damage', 'Verify brake fluid level, condition, and pedal feel'],
            Pending: ['Monitor brake pedal travel and ABS activation behaviour', 'Inspect suspension components for excessive wear or play'],
            Inactive: ['Note road conditions (wet, gravel) when fault triggered'],
            History: ['Confirm correct wheel speed sensor clearance after any suspension work'],
        },
    },
    {
        key: 'network', label: 'Vehicle Network', Icon: FaNetworkWired, prefix: 'U', min: 0, max: 9999,
        actions: {
            Active: ['Inspect CAN bus wiring, connectors, and termination resistors', 'Check all ECU chassis ground straps and supply voltage', 'Test inter-module communication using a bi-directional scan tool'],
            Pending: ['Monitor for intermittent comm drops under vibration or heat', 'Check battery voltage under cranking and charging load'],
            Inactive: ['Inspect connector pins for corrosion at the affected module'],
            History: ['Verify ECU firmware is current — check for pending calibration updates'],
        },
    },
];

const STATUS_PRIORITY = { Active: 0, Pending: 1, Inactive: 2, History: 3 };

function getRecommendations(signals) {
    const recs = [];
    for (const sys of SYSTEM_RECOMMENDATIONS) {
        const match = signals.filter((s) => {
            const code = (s.code || '').toUpperCase();
            if (code[0] !== sys.prefix) return false;
            const num = parseInt(code.slice(1), 10);
            return num >= sys.min && num <= sys.max;
        });
        if (!match.length) continue;
        const worst = match.reduce(
            (best, s) => (STATUS_PRIORITY[s.status] ?? 9) < (STATUS_PRIORITY[best] ?? 9) ? s.status : best,
            'History',
        );
        recs.push({
            ...sys,
            status: worst,
            count: match.length,
            specificCodes: match.map((s) => s.code).join(', '),
            actions: (sys.actions[worst] || sys.actions.History).slice(0, 3),
        });
    }
    return recs.sort((a, b) => (STATUS_PRIORITY[a.status] ?? 9) - (STATUS_PRIORITY[b.status] ?? 9));
}

function getSystemHealth(signals, system) {
    const match = signals.filter((s) => {
        const code = (s.code || '').toUpperCase();
        if (code[0] !== system.prefix) return false;
        const num = parseInt(code.slice(1), 10);
        return num >= system.min && num <= system.max;
    });
    if (!match.length) return { status: null, count: 0, hex: '#94a3b8', bg: '#f8fafc', border: '#e2e8f0', text: '#94a3b8' };
    const worst = match.reduce(
        (best, s) => ((STATUS_PRIORITY[s.status] ?? 9) < (STATUS_PRIORITY[best] ?? 9) ? s.status : best),
        match[0].status,
    );
    const cfg = STATUS_CONFIG[worst] || STATUS_CONFIG.History;
    return { status: worst, count: match.length, hex: cfg.hex, bg: cfg.bg, border: cfg.border, text: cfg.text };
}

const GAP = 3;
const DonutChart = ({ segments, total }) => {
    const R = 62, cx = 100, cy = 100;
    const C = 2 * Math.PI * R;
    let rotation = -90;
    const arcs = segments.map((seg) => {
        const pct = total > 0 ? seg.value / total : 0;
        const len = pct * C;
        const startRot = rotation;
        rotation += pct * 360;
        return { ...seg, len, startRot };
    });
    return (
        <svg viewBox="0 0 200 200" style={{ width: '100%', maxWidth: 180 }}>
            <circle cx={cx} cy={cy} r={R} fill="none" stroke="#f1f5f9" strokeWidth={26} />
            {arcs.map((arc, i) =>
                arc.len > GAP ? (
                    <circle key={i} cx={cx} cy={cy} r={R} fill="none" stroke={arc.color}
                        strokeWidth={26} strokeLinecap="butt"
                        strokeDasharray={`${arc.len - GAP} ${C - arc.len + GAP}`}
                        transform={`rotate(${arc.startRot} ${cx} ${cy})`}
                    />
                ) : null,
            )}
            <text x={cx} y={cy - 6} textAnchor="middle" fontSize="28" fontWeight="800" fill="#0f172a" fontFamily="Inter, sans-serif">{total}</text>
            <text x={cx} y={cy + 14} textAnchor="middle" fontSize="9" fill="#94a3b8" fontFamily="Inter, sans-serif" fontWeight="700" letterSpacing="1.5">SIGNALS</text>
        </svg>
    );
};

const SystemTile = ({ system, health }) => {
    const { Icon } = system;
    const hasData = health.status !== null;
    return (
        <div style={{ padding: '12px 8px 10px', borderRadius: 10, border: `1px solid ${health.border}`, background: health.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <div style={{ fontSize: 22, color: hasData ? health.hex : '#cbd5e1', lineHeight: 1 }}><Icon /></div>
            <div style={{ fontSize: 10, fontWeight: 700, color: hasData ? '#1e293b' : '#94a3b8', textAlign: 'center', lineHeight: 1.3 }}>{system.label}</div>
            {hasData ? (
                <div style={{ fontSize: 9, fontWeight: 700, color: health.text, background: `${health.hex}1a`, border: `1px solid ${health.hex}44`, borderRadius: 20, padding: '2px 8px', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>
                    {health.count}× {health.status}
                </div>
            ) : (
                <div style={{ fontSize: 9, color: '#cbd5e1' }}>—</div>
            )}
        </div>
    );
};

// The gauge arc spans 270°: starts at 7:30 (225° clockwise from 12 o'clock)
// and ends at 4:30 (135° clockwise from 12 o'clock).
// SVG rotate(135 cx cy) shifts the circle's native start (3 o'clock)
// clockwise by 135° → lands at 7:30 ✓
const HealthGauge = ({ score, displayScore }) => {
    const cx = 100, cy = 100, R = 68;
    const C = 2 * Math.PI * R;          // full circumference ≈ 427.3
    const ARC = C * 0.75;                  // 270° worth of arc
    const filled = score > 0 ? ARC * (score / 100) : 0;
    const color = getHealthColor(score);

    return (
        <svg viewBox="0 0 200 200" style={{ width: '100%', maxWidth: 200, animation: 'dtc-gauge-in 0.6s cubic-bezier(0.34,1.56,0.64,1) both' }}>
            {/* pulsing warning glow — only when critical */}
            {score < 40 && (
                <circle cx={cx} cy={cy} r={R} fill="none" stroke={color} strokeWidth={30}
                    strokeLinecap="round"
                    strokeDasharray={`${ARC} ${C - ARC}`}
                    transform={`rotate(135 ${cx} ${cy})`}
                    style={{ animation: 'dtc-pulse-warn 2s ease-in-out infinite' }}
                />
            )}
            {/* track */}
            <circle cx={cx} cy={cy} r={R} fill="none" stroke="#f1f5f9" strokeWidth={20}
                strokeLinecap="round"
                strokeDasharray={`${ARC} ${C - ARC}`}
                transform={`rotate(135 ${cx} ${cy})`}
            />
            {/* progress arc — CSS transition animates fill on score change */}
            <circle cx={cx} cy={cy} r={R} fill="none" stroke={color} strokeWidth={20}
                strokeLinecap="round"
                strokeDasharray={`${filled} ${C - filled}`}
                transform={`rotate(135 ${cx} ${cy})`}
                style={{ transition: 'stroke-dasharray 1.4s cubic-bezier(0.34,1.56,0.64,1), stroke 0.6s ease' }}
            />
            {/* subtle inner shadow ring */}
            <circle cx={cx} cy={cy} r={R - 13} fill="none" stroke="#f8fafc" strokeWidth={1} />
            {/* score number — count-up via displayScore */}
            <text x={cx} y={95} textAnchor="middle" fontSize="40" fontWeight="800" fill="#0f172a"
                fontFamily="Inter, sans-serif"
                style={{ animation: 'dtc-score-num 0.4s ease both 0.3s' }}>
                {displayScore}
            </text>
            {/* /100 */}
            <text x={cx} y={116} textAnchor="middle" fontSize="11" fontWeight="700" fill={color}
                fontFamily="Inter, sans-serif" letterSpacing="0.6">
                {getHealthLabel(score).toUpperCase()}
            </text>
            {/* range labels */}
            <text x={40} y={168} textAnchor="middle" fontSize="9" fill="#cbd5e1" fontFamily="Inter, sans-serif">0</text>
            <text x={160} y={168} textAnchor="middle" fontSize="9" fill="#cbd5e1" fontFamily="Inter, sans-serif">100</text>
        </svg>
    );
};

const PRIORITY_CONFIG = {
    Active: { label: 'Urgent', hex: '#ef4444', bg: '#fff5f5', border: '#fca5a5' },
    Pending: { label: 'Soon', hex: '#f59e0b', bg: '#fffbeb', border: '#fde68a' },
    Inactive: { label: 'Monitor', hex: '#0891b2', bg: '#ecfeff', border: '#a5f3fc' },
    History: { label: 'Review', hex: '#94a3b8', bg: '#f8fafc', border: '#e2e8f0' },
};

const PrioritySectionHeader = ({ status, count, groupIndex }) => {
    const cfg = PRIORITY_CONFIG[status] || PRIORITY_CONFIG.History;
    return (
        <div
            style={{
                display: 'flex', alignItems: 'center', gap: 8,
                animation: `dtc-rec-in 0.35s ease both ${groupIndex * 0.12}s`,
            }}
        >
            {/* colored square dot */}
            <div style={{ width: 9, height: 9, borderRadius: 2, background: cfg.hex, flexShrink: 0 }} />
            {/* label */}
            <span style={{ fontSize: 10, fontWeight: 800, color: cfg.hex, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                {cfg.label}
            </span>
            {/* count badge */}
            <span
                style={{
                    fontSize: 9, fontWeight: 700, color: cfg.hex,
                    background: `${cfg.hex}18`, border: `1px solid ${cfg.hex}44`,
                    borderRadius: 20, padding: '0 7px', lineHeight: '18px',
                }}
            >
                {count}
            </span>
            {/* divider line */}
            <div style={{ flex: 1, height: 1, background: `${cfg.hex}28`, borderRadius: 1 }} />
        </div>
    );
};

const RecommendationCard = ({ rec, index, groupIndex }) => {
    const { Icon } = rec;
    const cfg = PRIORITY_CONFIG[rec.status] || PRIORITY_CONFIG.History;
    const codes = rec.specificCodes ? rec.specificCodes.split(',').map((c) => c.trim()).filter(Boolean) : [];

    return (
        <div
            style={{
                display: 'flex',
                borderRadius: 10,
                border: '1px solid #e8edf4',
                background: '#fff',
                overflow: 'hidden',
                animation: `dtc-rec-in 0.45s ease both ${groupIndex * 0.12 + index * 0.07 + 0.08}s`,
                boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
            }}
        >
            {/* left accent bar */}
            <div style={{ width: 4, background: cfg.hex, flexShrink: 0 }} />

            {/* card body */}
            <div style={{ flex: 1, padding: '11px 14px', minWidth: 0 }}>
                {/* header: icon chip + name + code chips + fault count */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                    {/* system icon chip */}
                    <div
                        style={{
                            width: 36, height: 36, borderRadius: 9, flexShrink: 0,
                            background: cfg.bg, border: `1px solid ${cfg.border}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: cfg.hex, fontSize: 18,
                        }}
                    >
                        <Icon />
                    </div>

                    {/* name + DTC chips */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', lineHeight: 1.3, marginBottom: 5 }}>
                            {rec.label}
                        </div>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            {codes.map((code) => (
                                <span
                                    key={code}
                                    style={{
                                        fontSize: 9, fontWeight: 700, fontFamily: 'monospace',
                                        background: `${cfg.hex}14`, border: `1px solid ${cfg.hex}44`,
                                        color: cfg.hex, borderRadius: 4, padding: '1px 6px',
                                        letterSpacing: '0.04em',
                                    }}
                                >
                                    {code}
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* fault count badge */}
                    {codes.length > 0 && (
                        <div
                            style={{
                                flexShrink: 0, textAlign: 'center',
                                background: `${cfg.hex}12`, border: `1px solid ${cfg.hex}33`,
                                borderRadius: 8, padding: '3px 8px',
                            }}
                        >
                            <div style={{ fontSize: 14, fontWeight: 800, color: cfg.hex, lineHeight: 1 }}>{codes.length}</div>
                            <div style={{ fontSize: 8, color: cfg.hex, opacity: 0.75, letterSpacing: '0.04em', textTransform: 'uppercase' }}>fault{codes.length !== 1 ? 's' : ''}</div>
                        </div>
                    )}
                </div>

                {/* numbered action steps */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {rec.actions.map((action, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                            {/* number box */}
                            <div
                                style={{
                                    width: 18, height: 18, borderRadius: 5, flexShrink: 0,
                                    background: `${cfg.hex}18`, border: `1px solid ${cfg.hex}44`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 9, fontWeight: 800, color: cfg.hex, marginTop: 1,
                                }}
                            >
                                {i + 1}
                            </div>
                            <Text style={{ fontSize: 11, color: '#475569', lineHeight: 1.55, flex: 1 }}>{action}</Text>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const AllClearBanner = () => (
    <div
        style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            gap: 10, padding: '28px 16px', textAlign: 'center',
        }}
    >
        <div
            style={{
                width: 52, height: 52, borderRadius: 13,
                background: '#f0fdf4', border: '1px solid #86efac',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 26, color: '#16a34a',
            }}
        >
            <CheckCircleOutlined />
        </div>
        <Text style={{ fontSize: 13, fontWeight: 700, color: '#16a34a' }}>All systems nominal</Text>
        <Text style={{ fontSize: 11, color: '#94a3b8' }}>
            No active or pending faults detected. Keep up with regular maintenance intervals.
        </Text>
    </div>
);

const DTCSignals = () => {
    const {
        stsToken: credentials,
        vehicleSimulatorSettings: { serialNo, vehicleKey },
    } = useContext(SimulatorContext);

    const screens = useBreakpoint();

    const [dtcCode, setDtcCode] = useState('');
    const [description, setDescription] = useState('');
    const [status, setStatus] = useState('Active');
    const [displayScore, setDisplayScore] = useState(0);
    const [recModalOpen, setRecModalOpen] = useState(false);
    const animFrameRef = useRef(null);
    const prevScoreRef = useRef(0);

    const [sentSignals, setSentSignals] = useState(() => {
        const DATA_VERSION = 'v2';
        const saved = localStorage.getItem('dtc_sent_signals');
        const savedVersion = localStorage.getItem('dtc_sent_signals_version');
        if (saved && savedVersion === DATA_VERSION) return JSON.parse(saved);
        return [];  // Start empty; populate via simulation
    });

    useEffect(() => {
        let el = document.getElementById('dtc-signals-animations');
        if (!el) {
            el = document.createElement('style');
            el.id = 'dtc-signals-animations';
            document.head.appendChild(el);
        }
        el.textContent = ANIMATION_CSS;
        return () => { document.getElementById('dtc-signals-animations')?.remove(); };
    }, []);

    useEffect(() => {
        localStorage.setItem('dtc_sent_signals', JSON.stringify(sentSignals));
        localStorage.setItem('dtc_sent_signals_version', 'v2');
    }, [sentSignals]);

    const stats = useMemo(() => {
        const c = { Active: 0, Pending: 0, Inactive: 0, History: 0 };
        sentSignals.forEach((s) => { if (c[s.status] !== undefined) c[s.status]++; });
        return c;
    }, [sentSignals]);

    const healthScore = useMemo(
        () => computeHealthScore(stats.Active, stats.Pending, stats.Inactive, stats.History, sentSignals.length),
        [stats, sentSignals.length],
    );

    const recommendations = useMemo(() => getRecommendations(sentSignals), [sentSignals]);

    const donutSegments = useMemo(() => [
        { value: stats.Active, color: '#ef4444', label: 'Active' },
        { value: stats.Pending, color: '#f59e0b', label: 'Pending' },
        { value: stats.Inactive, color: '#22c55e', label: 'Inactive' },
        { value: stats.History, color: '#3b82f6', label: 'History' },
    ], [stats]);

    const systemHealth = useMemo(() =>
        VEHICLE_SYSTEMS.reduce((acc, sys) => ({ ...acc, [sys.key]: getSystemHealth(sentSignals, sys) }), {}),
        [sentSignals],
    );

    const prefixStats = useMemo(() => {
        const c = { P: 0, B: 0, C: 0, U: 0 };
        sentSignals.forEach((s) => {
            const p = (s.code || '')[0]?.toUpperCase();
            if (c[p] !== undefined) c[p]++;
        });
        return c;
    }, [sentSignals]);

    useEffect(() => {
        if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
        const startVal = prevScoreRef.current;
        const endVal = healthScore;
        const duration = 1300;
        const startTime = performance.now();

        const step = (now) => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);           // ease-out cubic
            setDisplayScore(Math.round(startVal + (endVal - startVal) * eased));
            if (progress < 1) {
                animFrameRef.current = requestAnimationFrame(step);
            } else {
                prevScoreRef.current = endVal;
            }
        };

        animFrameRef.current = requestAnimationFrame(step);
        return () => { if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current); };
    }, [healthScore]); // eslint-disable-line react-hooks/exhaustive-deps

    const { mutate: triggerSendSignal, isPending: sending } = useMutation({
        mutationFn: sendDTCSignal,
        onSuccess: (_, variables) => {
            const payload = variables.queryKey[2];
            message.success('DTC Signal sent successfully!');
            setSentSignals((prev) => [
                {
                    key: Date.now(), timestamp: Date.now(),
                    code: payload.data.code, description: payload.data.description, status: payload.data.status,
                },
                ...prev,
            ]);
            setDtcCode('');
            setDescription('');
        },
        onError: () => message.error('Failed to send DTC Signal.'),
    });

    const handleSendSignal = () => {
        if (!dtcCode) { message.warning('Please enter a DTC Code.'); return; }
        triggerSendSignal({
            queryKey: [
                'sendDTCSignal', credentials,
                { data: { code: dtcCode.toUpperCase(), description, status, timestamp: Date.now() }, id: generateUniqSerial(), serialNo, type: 'dtc', vehicleKey },
            ],
        });
    };

    const handleGenerateRandom = () => {
        const { code: c, description: d } = SAMPLE_DTCS[Math.floor(Math.random() * SAMPLE_DTCS.length)];
        setDtcCode(c);
        setDescription(d);
        setStatus(['Active', 'Pending', 'Inactive', 'History'][Math.floor(Math.random() * 4)]);
    };

    const columns = [
        {
            title: 'Timestamp', dataIndex: 'timestamp', key: 'timestamp', width: 170,
            render: (ts) => new Date(new Date(ts).getTime() + 5.5 * 3600000).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }),
            sorter: (a, b) => a.timestamp - b.timestamp, defaultSortOrder: 'descend',
        },
        {
            title: 'DTC Code', dataIndex: 'code', key: 'code', width: 100,
            render: (text) => (
                <Text strong style={{ fontFamily: 'monospace', fontSize: 13, background: '#f1f5f9', padding: '2px 8px', borderRadius: 4, border: '1px solid #e2e8f0' }}>
                    {text}
                </Text>
            ),
        },
        {
            title: 'Description', dataIndex: 'description', key: 'description',
            render: (text) => <Text style={{ fontSize: 12 }}>{text || '—'}</Text>,
        },
        {
            title: 'Status', dataIndex: 'status', key: 'status', width: 100,
            render: (s) => <Tag color={STATUS_CONFIG[s]?.color || 'default'} style={{ borderRadius: 20, fontSize: 11 }}>{s}</Tag>,
            filters: ['Active', 'Pending', 'Inactive', 'History'].map((s) => ({ text: s, value: s })),
            onFilter: (value, record) => record.status === value,
        },
    ];

    const prefixBars = [
        { prefix: 'P', label: 'Powertrain', color: '#ef4444' },
        { prefix: 'B', label: 'Body', color: '#7c3aed' },
        { prefix: 'C', label: 'Chassis', color: '#0369a1' },
        { prefix: 'U', label: 'Network', color: '#6366f1' },
    ];

    const selectedStatusCfg = STATUS_CONFIG[status] || {};
    const scoreColor = getHealthColor(healthScore);
    const scoreLabel = getHealthLabel(healthScore);

    const SCORE_COPY = {
        Excellent: 'Vehicle is in excellent condition. All systems nominal.',
        Good: 'Vehicle health is good. Minor issues noted.',
        Fair: 'Some faults detected. Service recommended soon.',
        Warning: 'Multiple faults active. Schedule service promptly.',
        Critical: 'Critical faults detected. Immediate attention required.',
    };

    return (
        <>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 20, flexWrap: 'wrap', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 38, height: 38, borderRadius: 10, background: '#fffbeb', border: '1px solid #fde68a', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <WarningOutlined style={{ fontSize: 18, color: '#d97706' }} />
                    </div>
                    <div>
                        <Title level={4} style={{ margin: 0 }}>DTC Signal Generator</Title>
                        {serialNo && <Text style={{ fontSize: 12, color: '#94a3b8', fontFamily: 'monospace' }}>{serialNo}</Text>}
                    </div>
                </div>
            </div>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
                {[
                    { label: 'Total', value: sentSignals.length, hex: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
                    { label: 'Active', value: stats.Active, hex: '#ef4444', bg: '#fff5f5', border: '#fca5a5' },
                    { label: 'Pending', value: stats.Pending, hex: '#d97706', bg: '#fffbeb', border: '#fde68a' },
                    { label: 'Inactive', value: stats.Inactive, hex: '#16a34a', bg: '#f0fdf4', border: '#86efac' },
                    { label: 'History', value: stats.History, hex: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
                ].map((card) => (
                    <div key={card.label} style={{ flex: '1 1 80px', minWidth: 72, padding: '10px 14px', borderRadius: 10, border: `1px solid ${card.border}`, background: card.bg }}>
                        <div style={{ fontSize: 26, fontWeight: 800, color: card.hex, lineHeight: 1 }}>{card.value}</div>
                        <div style={{ fontSize: 10, fontWeight: 700, color: card.hex, opacity: 0.75, textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 4 }}>{card.label}</div>
                    </div>
                ))}
            </div>

            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>

                <Col xs={24} sm={9} lg={7} style={{ display: 'flex' }}>
                    <Card
                        title={
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span>Vehicle Health</span>
                                <Tag
                                    style={{
                                        margin: 0, borderRadius: 20, fontSize: 9, fontWeight: 700,
                                        background: `${scoreColor}18`, border: `1px solid ${scoreColor}55`, color: scoreColor,
                                    }}
                                >
                                    {scoreLabel}
                                </Tag>
                            </div>
                        }
                        style={{ borderRadius: 12, border: `1px solid ${scoreColor}44`, width: '100%' }}
                        styles={{ body: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 } }}
                    >
                        <HealthGauge score={healthScore} displayScore={displayScore} />

                        <div
                            style={{
                                width: '100%', padding: '8px 12px', borderRadius: 8,
                                background: `${scoreColor}0f`, border: `1px solid ${scoreColor}33`,
                                textAlign: 'center',
                            }}
                        >
                            <Text style={{ fontSize: 11, color: scoreColor, fontWeight: 600, lineHeight: 1.5 }}>
                                {SCORE_COPY[scoreLabel]}
                            </Text>
                        </div>

                        {(stats.Active > 0 || stats.Pending > 0) && (
                            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 6 }}>
                                <Divider style={{ margin: '0 0 2px' }}>
                                    <Text style={{ fontSize: 10, color: '#cbd5e1', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                                        Score impact
                                    </Text>
                                </Divider>
                                {stats.Active > 0 && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 10px', borderRadius: 7, background: '#fff5f5', border: '1px solid #fca5a5' }}>
                                        <Text style={{ fontSize: 11, color: '#64748b' }}>{stats.Active} active fault{stats.Active !== 1 ? 's' : ''}</Text>
                                        <Text style={{ fontSize: 11, fontWeight: 700, color: '#ef4444' }}>−{Math.min(stats.Active, 5) * 8 + Math.max(0, stats.Active - 5) * 2} pts</Text>
                                    </div>
                                )}
                                {stats.Pending > 0 && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 10px', borderRadius: 7, background: '#fffbeb', border: '1px solid #fde68a' }}>
                                        <Text style={{ fontSize: 11, color: '#64748b' }}>{stats.Pending} pending fault{stats.Pending !== 1 ? 's' : ''}</Text>
                                        <Text style={{ fontSize: 11, fontWeight: 700, color: '#d97706' }}>−{Math.min(stats.Pending, 5) * 4} pts</Text>
                                    </div>
                                )}
                            </div>
                        )}
                    </Card>
                </Col>

                <Col xs={24} sm={15} lg={17} style={{ display: 'flex' }}>
                    <Card
                        title={
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span>Recommended Actions</span>
                            </div>
                        }
                        extra={
                            recommendations.length > 0 && (
                                <div style={{ display: 'flex', gap: 6 }}>
                                    {['Active', 'Pending', 'Inactive', 'History'].map((s) => {
                                        const count = recommendations.filter((r) => r.status === s).length;
                                        if (!count) return null;
                                        const cfg = PRIORITY_CONFIG[s];
                                        return (
                                            <Tag key={s} style={{ margin: 0, borderRadius: 20, fontSize: 9, background: `${cfg.hex}18`, border: `1px solid ${cfg.hex}44`, color: cfg.hex }}>
                                                {count} {cfg.label}
                                            </Tag>
                                        );
                                    })}
                                </div>
                            )
                        }
                        style={{ borderRadius: 12, border: '1px solid #e2e8f0', width: '100%' }}
                        styles={{ body: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 20, padding: '28px 24px' } }}
                    >
                        {recommendations.length === 0 ? (
                            <AllClearBanner />
                        ) : (
                            <>
                                <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    {['Active', 'Pending', 'Inactive', 'History'].map((s) => {
                                        const group = recommendations.filter((r) => r.status === s);
                                        if (!group.length) return null;
                                        const cfg = PRIORITY_CONFIG[s];
                                        return (
                                            <div
                                                key={s}
                                                style={{
                                                    display: 'flex', alignItems: 'center', gap: 10,
                                                    padding: '8px 12px', borderRadius: 9,
                                                    background: cfg.bg, border: `1px solid ${cfg.border}`,
                                                }}
                                            >
                                                <div style={{ width: 8, height: 8, borderRadius: 2, background: cfg.hex, flexShrink: 0 }} />
                                                <span style={{ fontSize: 11, fontWeight: 700, color: cfg.hex, flex: 1 }}>
                                                    {cfg.label}
                                                </span>
                                                <span style={{ fontSize: 11, color: '#64748b', flex: 3 }}>
                                                    {group.map((r) => r.label).join(', ')}
                                                </span>
                                                <span
                                                    style={{
                                                        fontSize: 10, fontWeight: 700, color: cfg.hex,
                                                        background: `${cfg.hex}18`, border: `1px solid ${cfg.hex}44`,
                                                        borderRadius: 20, padding: '0 8px', lineHeight: '18px', flexShrink: 0,
                                                    }}
                                                >
                                                    {group.length} system{group.length !== 1 ? 's' : ''}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>

                                <Button
                                    type="primary"
                                    size="large"
                                    style={{
                                        borderRadius: 10, fontWeight: 700, fontSize: 13,
                                        background: '#2563eb', borderColor: '#2563eb',
                                        paddingInline: 32, height: 44,
                                    }}
                                    onClick={() => setRecModalOpen(true)}
                                >
                                    View Recommended Actions
                                </Button>
                            </>
                        )}
                    </Card>
                </Col>
            </Row>

            <Modal
                open={recModalOpen}
                onCancel={() => setRecModalOpen(false)}
                footer={null}
                width={680}
                styles={{ body: { padding: '4px 0 8px', maxHeight: '72vh', overflowY: 'auto' } }}
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div
                            style={{
                                width: 30, height: 30, borderRadius: 8,
                                background: '#fffbeb', border: '1px solid #fde68a',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 15, color: '#d97706',
                            }}
                        >
                            <WarningOutlined />
                        </div>
                        <span style={{ fontSize: 15, fontWeight: 700 }}>Recommended Actions</span>
                        <div style={{ display: 'flex', gap: 5, marginLeft: 4 }}>
                            {['Active', 'Pending', 'Inactive', 'History'].map((s) => {
                                const count = recommendations.filter((r) => r.status === s).length;
                                if (!count) return null;
                                const cfg = PRIORITY_CONFIG[s];
                                return (
                                    <Tag key={s} style={{ margin: 0, borderRadius: 20, fontSize: 9, fontWeight: 700, background: `${cfg.hex}18`, border: `1px solid ${cfg.hex}44`, color: cfg.hex }}>
                                        {count} {cfg.label}
                                    </Tag>
                                );
                            })}
                        </div>
                    </div>
                }
            >
                <div style={{ padding: '8px 24px 16px', display: 'flex', flexDirection: 'column', gap: 20 }}>
                    {['Active', 'Pending', 'Inactive', 'History'].map((s, gIdx) => {
                        const group = recommendations.filter((r) => r.status === s);
                        if (!group.length) return null;
                        return (
                            <div key={s} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                <PrioritySectionHeader status={s} count={group.length} groupIndex={gIdx} />
                                {group.map((rec, i) => (
                                    <RecommendationCard key={rec.key} rec={rec} index={i} groupIndex={gIdx} />
                                ))}
                            </div>
                        );
                    })}
                </div>
            </Modal>

            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>

                <Col xs={24} sm={10} lg={8} style={{ display: 'flex' }}>
                    <Card
                        title="Status Distribution"
                        style={{ borderRadius: 12, border: '1px solid #e2e8f0', width: '100%' }}
                        styles={{ body: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 } }}
                    >
                        <DonutChart segments={donutSegments} total={sentSignals.length} />
                        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {donutSegments.map((seg) => {
                                const pct = sentSignals.length > 0 ? Math.round((seg.value / sentSignals.length) * 100) : 0;
                                return (
                                    <div key={seg.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <div style={{ width: 10, height: 10, borderRadius: 3, background: seg.color, flexShrink: 0 }} />
                                        <span style={{ fontSize: 12, color: '#64748b', flex: 1 }}>{seg.label}</span>
                                        <span style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{seg.value}</span>
                                        <span style={{ fontSize: 10, color: '#94a3b8', width: 34, textAlign: 'right' }}>{pct}%</span>
                                    </div>
                                );
                            })}
                        </div>
                    </Card>
                </Col>

                <Col xs={24} sm={14} lg={16} style={{ display: 'flex' }}>
                    <Card
                        title="Vehicle System Health"
                        style={{ borderRadius: 12, border: '1px solid #e2e8f0', width: '100%' }}
                        styles={{ body: { display: 'flex', flexDirection: 'column', gap: 16 } }}
                        extra={
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                {prefixBars.map((p) => (
                                    <div key={p.prefix} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <div style={{ width: 8, height: 8, borderRadius: 2, background: p.color, flexShrink: 0 }} />
                                        <span style={{ fontSize: 10, color: '#64748b' }}>{p.prefix}</span>
                                        <span style={{ fontSize: 10, fontWeight: 700, color: '#0f172a' }}>{prefixStats[p.prefix]}</span>
                                    </div>
                                ))}
                            </div>
                        }
                    >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                            {prefixBars.map((p) => {
                                const pct = sentSignals.length > 0 ? (prefixStats[p.prefix] / sentSignals.length) * 100 : 0;
                                return (
                                    <div key={p.prefix} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                        <span style={{ fontSize: 11, fontWeight: 800, color: p.color, width: 14, flexShrink: 0 }}>{p.prefix}</span>
                                        <span style={{ fontSize: 10, color: '#64748b', width: 72, flexShrink: 0 }}>{p.label}</span>
                                        <div style={{ flex: 1, height: 6, borderRadius: 3, background: '#f1f5f9', overflow: 'hidden' }}>
                                            <div style={{ height: '100%', width: `${pct}%`, background: p.color, borderRadius: 3, transition: 'width 0.6s ease', minWidth: pct > 0 ? 6 : 0 }} />
                                        </div>
                                        <span style={{ fontSize: 11, fontWeight: 700, color: '#0f172a', width: 22, textAlign: 'right', flexShrink: 0 }}>{prefixStats[p.prefix]}</span>
                                    </div>
                                );
                            })}
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 10 }}>
                            {VEHICLE_SYSTEMS.map((sys) => (
                                <SystemTile key={sys.key} system={sys} health={systemHealth[sys.key]} />
                            ))}
                        </div>
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]}>

                <Col xs={24} lg={10}>
                    <Card
                        title="Generate Signal"
                        style={{ borderRadius: 12, border: '1px solid #e2e8f0' }}
                        extra={
                            <Button type="dashed" icon={<RetweetOutlined />} onClick={handleGenerateRandom} size="small">
                                Random
                            </Button>
                        }
                    >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                            <div>
                                <Text style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>DTC Code</Text>
                                <Input placeholder="e.g. P0300" value={dtcCode} onChange={(e) => setDtcCode(e.target.value)} maxLength={10} style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 14 }} />
                            </div>
                            <div>
                                <Text style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>Description</Text>
                                <TextArea placeholder="Enter description..." value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
                            </div>
                            <div>
                                <Text style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>Status</Text>
                                <Select value={status} onChange={setStatus} style={{ width: '100%' }}>
                                    {['Active', 'Pending', 'Inactive', 'History'].map((s) => (
                                        <Option key={s} value={s}>
                                            <Tag color={STATUS_CONFIG[s]?.color} style={{ borderRadius: 20, fontSize: 11, margin: 0 }}>{s}</Tag>
                                        </Option>
                                    ))}
                                </Select>
                                {selectedStatusCfg.bg && (
                                    <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 8, background: selectedStatusCfg.bg, border: `1px solid ${selectedStatusCfg.border}` }}>
                                        <Text style={{ fontSize: 11, color: selectedStatusCfg.text }}>
                                            {status === 'Active' && 'Fault currently present and confirmed by ECU. Triggers Check Engine light.'}
                                            {status === 'Pending' && 'Anomaly detected but not yet confirmed. Check Engine light usually off.'}
                                            {status === 'Inactive' && 'Fault was active previously but is not currently present.'}
                                            {status === 'History' && 'Permanent record of past issues kept for long-term vehicle health reporting.'}
                                        </Text>
                                    </div>
                                )}
                            </div>
                            <Button type="primary" icon={<SendOutlined />} onClick={handleSendSignal} loading={sending} block size="large">
                                Send Signal
                            </Button>
                        </div>
                    </Card>
                </Col>

                <Col xs={24} lg={14}>
                    <Card
                        title="Sent Signals"
                        style={{ borderRadius: 12, border: '1px solid #e2e8f0' }}
                        extra={<Tag color="blue" style={{ borderRadius: 20, fontSize: 10 }}>{sentSignals.length} signal{sentSignals.length !== 1 ? 's' : ''}</Tag>}
                    >
                        <Table
                            dataSource={sentSignals}
                            columns={columns}
                            pagination={{ pageSize: 6, showSizeChanger: false, size: 'small' }}
                            size="small"
                            scroll={{ x: 500 }}
                        />
                    </Card>
                </Col>
            </Row>
        </>
    );
};

export default DTCSignals;
